import { useState } from "react";
import Draggable from "react-draggable";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { useAppDispatch } from "store/hooks";
import { useI18n } from "i18n";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Select } from "@remo-co/ui-core/src/components/Select";
import {
  selectCurrentTheaterId,
  selectTheaterSpaces,
  selectTheaterTemplate,
} from "modules/theater/selectors";
import { useSelector } from "react-redux";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { useFloorNames } from "modules/floors/hooks/useFloorNames";
import { IMapTemplate } from "types/theater";
import { selectIsEnforceTableAssignmentScheduled } from "modules/enforceTableAssignment/redux";
import {
  setDialogVisible,
  setHoveredTableCode,
  setHoveredUserId,
} from "../../redux";
import { useEventTableSettingPresetOperations } from "../../hooks/useEventTableSettingPresetOperations";
import { useEventTableSettingPresets } from "../../hooks/useEventTableSettingPresets";
import { useEventTableSettings } from "../../hooks/useEventTableSettings";
import { useUpdateEventTableSettings } from "../../hooks/useUpdateEventTableSettings";
import { PresetSelect } from "../PresetSelect";
import { AttendeeList } from "../AttendeeList";
import useStyles from "./styles";
import { TableGrid } from "../TableGrid";
import { AttendeeManagementActions } from "../AttendeeManagementActions";
import { LocateAttendeeHandler } from "../LocateAttendeeHandler";

interface Props {
  eventId: string;
}

export const InEventSeatManagementDialog = ({ eventId }: Props) => {
  const isEnforceTableAssignmentScheduled = useSelector(
    selectIsEnforceTableAssignmentScheduled,
  );
  const currentFloorId = useSelector(selectCurrentSpaceId);
  const floors = useSelector(selectTheaterSpaces);
  const theaterId = useSelector(selectCurrentTheaterId);
  const template = useSelector(selectTheaterTemplate);
  const floorNames = useFloorNames(theaterId);
  const [selectedPreset, setSelectedPreset] = useState("default");
  const { presets } = useEventTableSettingPresets(eventId);
  const { tableData, assignedAttendees } = useEventTableSettings(
    eventId,
    selectedPreset === "default" ? undefined : selectedPreset,
  );
  const {
    isLoading: operationsLoading,
    createPreset,
    deletePreset,
  } = useEventTableSettingPresetOperations(eventId, {
    onCreateSuccess: (incomingPreset: string) => {
      setSelectedPreset(incomingPreset);
    },
    onDeleteSuccess: () => {
      setSelectedPreset("default");
    },
  });
  const {
    addAttendeeToTable,
    moveAttendeeToTable,
    removeAttendeeFromTable,
    unassignAttendeesFromTable,
  } = useUpdateEventTableSettings(
    eventId,
    selectedPreset === "default" ? undefined : selectedPreset,
  );
  const [selectedFloorId, setFloorId] = useState(currentFloorId);
  const dispatch = useAppDispatch();
  const { t } = useI18n(["event", "common"]);
  const styles = useStyles();

  const closeDialog = () => {
    dispatch(setDialogVisible(false));
  };

  const handleSetHoveredTableCode = (tableCode: string | null) => {
    dispatch(setHoveredTableCode(tableCode));
  };
  const handleSetHoveredUserId = (id: string | null) => {
    dispatch(setHoveredUserId(id));
  };

  if (!selectedFloorId || !template) {
    return null;
  }

  return (
    <>
      <Draggable handle={`.${styles.header}`}>
        <Container className={styles.content}>
          <Box display="flex" className={styles.header}>
            <Box display="flex" className={styles.headerContent}>
              <Typography variant="h4" className={styles.title}>
                {t("event:assigned.shuffle")}
              </Typography>
              <Box display="flex" className={styles.settings}>
                <PresetSelect
                  presets={presets}
                  selectedPreset={selectedPreset}
                  handlePresetChange={(preset) => setSelectedPreset(preset)}
                  createPreset={createPreset}
                  deletePreset={deletePreset}
                  isLoading={operationsLoading}
                  isInEvent
                  disabled={isEnforceTableAssignmentScheduled}
                />
                <Box className={styles.select}>
                  <Select
                    size="small"
                    value={selectedFloorId}
                    onChange={setFloorId}
                    disabled={isEnforceTableAssignmentScheduled}
                    options={floors.map((key, index) => ({
                      value: key,
                      key,
                      label:
                        floorNames[key] ||
                        t("manageEvent:floor.number", {
                          number: index + 1,
                        }),
                    }))}
                    inModal
                  />
                </Box>

                <span className={styles.actions}>
                  <AttendeeManagementActions
                    eventId={eventId}
                    selectedPreset={selectedPreset}
                    disabled={
                      assignedAttendees.length < 2 ||
                      isEnforceTableAssignmentScheduled
                    }
                    isInEvent
                  />
                </span>
              </Box>
            </Box>
            <Tooltip title={t("common:button.close")} placement="top">
              <Button
                variant="ghost"
                color="dark"
                size="sm"
                onClick={closeDialog}
                isIconButton
                data-testid="close-dialog-button"
                disabled={isEnforceTableAssignmentScheduled}
              >
                <Close data-testid="close-icon" />
              </Button>
            </Tooltip>
          </Box>
          <Box display="flex" className={styles.container}>
            <div className={styles.leftPanel}>
              <AttendeeList
                eventId={eventId}
                assignedAttendees={assignedAttendees}
                removeAttendeeFromTable={removeAttendeeFromTable}
                setHoveredUserId={handleSetHoveredUserId}
                size="sm"
              />
            </div>
            <div className={styles.grid}>
              <TableGrid
                selectedFloorId={selectedFloorId}
                tableData={tableData}
                eventId={eventId}
                hasMultipleFloors={floors.length > 1}
                templateTables={(template as IMapTemplate).rooms}
                selectedPreset={selectedPreset}
                setHoveredTableCode={handleSetHoveredTableCode}
                setHoveredUserId={handleSetHoveredUserId}
                addAttendeeToTable={addAttendeeToTable}
                moveAttendeeToTable={moveAttendeeToTable}
                unassignAttendeesFromTable={unassignAttendeesFromTable}
                isInEvent
              />
            </div>
          </Box>
        </Container>
      </Draggable>
      <LocateAttendeeHandler />
    </>
  );
};
